/**
 * COACHDIA Pulse — Briefing Room (Iter289 — IA À LA DEMANDE)
 * ============================================================
 *
 * Pivot architecture : abandon de la narration continue.
 *   • L'IA ne parle JAMAIS sans question utilisateur.
 *   • Affichage UNE section à la fois (PulseReportView mode='single').
 *   • Cockpit fixe en bas : navigation + actions IA à la demande.
 *   • 2 tools LLM : focus_section + get_section_context.
 *   • 0 auto-continuation, 0 boucle "merci à bientôt".
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Mic, Loader2, Square, ArrowLeft, Sparkles,
  Volume2, Ear, Brain, CircleDot, X, Mail, Send,
  FileText, AlertCircle, CheckCircle2, Radio,
} from 'lucide-react';
import { getApiUrl } from '../config/environment';
import { PulseBriefingAudioChat } from '../components/pulse/briefing/PulseBriefingAudioChat';
import { PulseReportView } from './TeamPulse';

const API = getApiUrl();
const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const COLORS = { espresso: '#1F1A12', gold: '#F3D060', darkGold: '#92710A', cream: '#F5F1EA' };

// Iter289 — Ordre canonique des 15 sections + libellés FR (cockpit nav)
const SECTION_ORDER = [
  'tldr', 'stance', 'economic', 'exec', 'dna', 'dyn', 'risks', 'loops',
  'triggers', 'levers', 'matrix', 'plan', 'members', 'scenar', 'coach',
];
const SECTIONS_LABELS = {
  tldr: 'Résumé exécutif',
  stance: 'Position stratégique',
  economic: 'Impact économique',
  exec: 'Synthèse exécutive',
  dna: 'ADN comportemental',
  dyn: 'Dynamique interne',
  risks: 'Risques',
  loops: 'Boucles systémiques',
  triggers: 'Déclencheurs de décision',
  levers: 'Leviers d\'action',
  matrix: 'Matrice Impact / Effort',
  plan: 'Plan 90 jours',
  members: 'Guide par membre',
  scenar: 'Scénarios',
  coach: 'Recommandation coaching',
};

const STATE_LABELS = {
  idle:             { lbl: 'Prêt',          icon: CircleDot, color: '#8A7A5E' },
  connecting:       { lbl: 'Connexion…',    icon: Loader2,   color: COLORS.darkGold, spin: true },
  listening:        { lbl: 'IA écoute',     icon: Ear,       color: '#3B6B8F' },
  listening_active: { lbl: 'Vous parlez…',  icon: Mic,       color: '#B94A48' },
  thinking:         { lbl: 'IA réfléchit',  icon: Brain,     color: COLORS.darkGold, spin: true },
  speaking:         { lbl: 'IA parle',      icon: Volume2,   color: '#5B8A6B' },
};

export default function PulseBriefingRoom() {
  const { teamId } = useParams();
  const [params] = useSearchParams();
  const batchId = params.get('batch_id') || params.get('batch') || '';
  const navigate = useNavigate();

  const [ctx, setCtx] = useState(null);
  const [batch, setBatch] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState('idle');
  const [transcripts, setTranscripts] = useState([]);

  // 🆕 Iter289 — État simplifié IA à la demande
  const [focusedSection, setFocusedSection] = useState('tldr');
  // Toggle micro premium : seule voie d'ouverture du mic
  const [micOpen, setMicOpen] = useState(false);
  // Iter291 — chrono d'enregistrement (timestamp ms du démarrage)
  const [recordingStart, setRecordingStart] = useState(null);
  // Iter291 — raison de réponse incomplète (déclenche le bouton "Continuer")
  const [incompleteReason, setIncompleteReason] = useState(null);
  // Dialog "Poser une question" (texte)
  const [askOpen, setAskOpen] = useState(false);
  // Menu sections (cliquable depuis cockpit pour navigation directe)
  const [sectionsMenuOpen, setSectionsMenuOpen] = useState(false);

  const [synthesisOpen, setSynthesisOpen] = useState(false);
  const [synthesis, setSynthesis] = useState(null);
  const [synthLoading, setSynthLoading] = useState(false);

  const [phase, setPhase] = useState('preroom');
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  // Iter315 — Source de vérité unique de la lecture audio (analyser Web Audio).
  // 'idle' | 'speaking' | 'paused'. Pilote l'affichage Pause/Resume/Stop
  // indépendamment de `state` (lié aux events Realtime, qui peuvent passer
  // à 'idle' avant la fin réelle de la lecture).
  const [playbackState, setPlaybackState] = useState('idle');
  // Iter301 — État réseau temps-réel : 'new' | 'connecting' | 'connected' |
  // 'disconnected' | 'failed' | 'closed'. Affiché dans le cockpit comme
  // point de santé (vert / jaune / rouge).
  const [connState, setConnState] = useState('new');

  const chatRef = useRef(null);
  const transcriptEndRef = useRef(null);
  const sessionStartRef = useRef(null);

  /* ---------- Chargement context + batch + report ----------
   * Contrat backend strict (zéro fallback) :
   *   GET /api/pulse-briefing/context/{batchId} → { team_name, wave_number, instructions, tools, voice, ... }
   *   GET /api/team-pulse/batches/{batchId}     → { batch:{ ..., report_data:{...} }, invitations:[], progress:{} }
   *
   * Tout écart au contrat lève une erreur explicite (loadError) qui s'affiche
   * dans une UI de diagnostic — JAMAIS de return null silencieux.
   */
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!batchId) {
        toast.error('Aucun rapport sélectionné.');
        navigate(`/team-pulse/teams/${teamId}`);
        return;
      }
      try {
        const [ctxR, batchR] = await Promise.all([
          axios.get(`${API}/pulse-briefing/context/${batchId}`, { headers: authHeaders() }),
          axios.get(`${API}/team-pulse/batches/${batchId}`, { headers: authHeaders() }),
        ]);
        if (!mounted) return;

        // Contrat strict — wrapper { batch, invitations, progress }
        const wrapper = batchR.data;
        if (!wrapper || typeof wrapper !== 'object' || !wrapper.batch) {
          throw new Error(
            `Contrat API rompu : GET /team-pulse/batches/${batchId} doit renvoyer { batch: {...} }. ` +
            `Reçu : ${JSON.stringify(Object.keys(wrapper || {}))}`
          );
        }
        const batchObj = wrapper.batch;
        const reportData = batchObj.report_data;
        if (!reportData) {
          throw new Error(
            'Le rapport n\'a pas encore été généré pour ce batch (report_data absent). ' +
            'Lancez la génération depuis le tableau de bord avant d\'entrer en salle.'
          );
        }

        const ctxData = ctxR.data;
        if (!ctxData?.instructions || !ctxData?.tools) {
          throw new Error(
            'Contrat API rompu : GET /pulse-briefing/context/{id} doit fournir instructions + tools.'
          );
        }

        setCtx(ctxData);
        setBatch(batchObj);
        setReport(reportData);
      } catch (e) {
        if (!mounted) return;
        const detail = e?.response?.data?.detail;
        const msg = typeof detail === 'string' ? detail : (e?.message || 'Erreur inconnue.');
        // eslint-disable-next-line no-console
        console.error('🔴 [PulseBriefingRoom] loadError:', msg, e);
        setLoadError(msg);
        toast.error(msg);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [batchId, teamId, navigate]);

  /* ---------- Tool call handler (Iter289 — 2 tools : focus_section + get_section_context) ---------- */
  const handleToolCall = useCallback(async (name, args, callId) => {
    try {
      // 🔥 focus_section : LLM cite une autre section que celle affichée
      if (name === 'focus_section') {
        const key = String(args?.section_key || '').trim();
        if (key) setFocusedSection(key);
        const r = await axios.post(`${API}/pulse-briefing/tools/${batchId}`,
          { name, arguments: args || {} }, { headers: authHeaders() });
        // expectResponse: false → focus_section est PASSIF, on n'enchaîne
        // pas de response.create. Le LLM continue son tour normalement.
        await chatRef.current?.submitToolResult(callId, String(r.data?.result || 'ok'), { expectResponse: false });
        return;
      }

      // get_section_context : LLM demande le payload brut → retour + response.create
      const r = await axios.post(`${API}/pulse-briefing/tools/${batchId}`,
        { name, arguments: args || {} }, { headers: authHeaders() });
      const result = r.data?.result;
      const serialized = (result === null || result === undefined)
        ? '(aucune donnée)'
        : (typeof result === 'string' ? result : JSON.stringify(result));
      await chatRef.current?.submitToolResult(callId, serialized);
    } catch (e) {
      await chatRef.current?.submitToolResult(callId, `Erreur tool: ${e?.message || 'inconnue'}`);
    }
  }, [batchId]);

  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [transcripts]);

  /* ---------- Démarrage / arrêt session ---------- */
  const startBriefing = async () => {
    if (!ctx) return;
    setTranscripts([]);
    setFocusedSection('tldr');

    const chat = new PulseBriefingAudioChat({
      apiBase: API.replace(/\/api$/, ''),
      token: localStorage.getItem('token'),
      onState: (s) => {
        setState(s);
      },
      onTranscript: ({ role, text, final }) => {
        if (role === 'user' && final) {
          const trimmed = (text || '').trim();
          if (trimmed.length < 2) return;
        }
        setTranscripts((prev) => {
          if (role === 'assistant' && !final && prev.length && prev[prev.length - 1].role === 'assistant' && prev[prev.length - 1].streaming) {
            return [...prev.slice(0, -1), { role, text, streaming: true }];
          }
          if (role === 'assistant' && final) {
            const copy = prev.slice();
            if (copy.length && copy[copy.length - 1].role === 'assistant' && copy[copy.length - 1].streaming) {
              copy[copy.length - 1] = { role, text, streaming: false };
              return copy;
            }
            return [...copy, { role, text, streaming: false }];
          }
          return [...prev, { role, text, streaming: !final, ts: Date.now() }];
        });
      },
      onToolCall: handleToolCall,
      onError: (err) => toast.error(err?.message || 'Erreur session IA'),
      onResponseIncomplete: (reason, opts = {}) => {
        // Iter391 — Trois modes possibles :
        //   1. opts.complete === true  → fin propre d'une chaîne auto-continue,
        //      on nettoie l'indicateur discret.
        //   2. opts.auto === true      → auto-continue silencieux en cours,
        //      indicateur discret "L'IA poursuit son analyse…" affiché.
        //      AUCUN bouton manuel, AUCUN toast intrusif.
        //   3. par défaut (manuel)     → plafond auto-continue atteint OU
        //      cas non couvert. Bouton "Continuer la réponse" + toast.
        if (opts.complete === true) {
          setIncompleteReason(null);
          return;
        }
        if (opts.auto === true) {
          setIncompleteReason(`auto:${opts.attempt || 1}/${opts.max || 3}`);
          return;
        }
        setIncompleteReason(reason || 'limite atteinte');
        toast.message('Réponse tronquée', {
          description: 'Cliquez sur « Continuer la réponse » pour la suite.',
        });
      },
      onConnectionState: setConnState,
      // Iter315 — Source de vérité unique de la lecture audio TTS, basée sur
      // l'analyse temps-réel du niveau audio de la MediaStream remote.
      onPlaybackState: setPlaybackState,
    });
    chatRef.current = chat;

    try {
      await chat.start({
        instructions: ctx.instructions,
        tools: ctx.tools,
        voice: ctx.voice || 'cedar',
        temperature: ctx.temperature ?? 0.85,
        turn_detection: ctx.turn_detection,
      });
      sessionStartRef.current = Date.now();
      setPhase('session');
      toast.success('Briefing prêt — posez votre question quand vous voulez.');
    } catch {
      chatRef.current = null;
    }
  };

  const endBriefing = async () => {
    if (chatRef.current) { await chatRef.current.stop(); chatRef.current = null; }
    const nonEmpty = transcripts.filter((t) => (t.text || '').trim());
    if (nonEmpty.length < 2) {
      toast('Session trop courte pour générer une synthèse.');
      navigate(`/team-pulse/teams/${teamId}`);
      return;
    }
    setSynthesisOpen(true);
    setSynthLoading(true);
    setSynthesis(null);
    try {
      const durationSec = sessionStartRef.current ? Math.round((Date.now() - sessionStartRef.current) / 1000) : null;
      const r = await axios.post(`${API}/pulse-briefing/synthesize/${batchId}`, {
        transcript: nonEmpty.map((t) => ({ role: t.role, text: t.text })),
        duration_seconds: durationSec,
      }, { headers: authHeaders() });
      setSynthesis(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Synthèse impossible.');
      setSynthesisOpen(false);
    } finally {
      setSynthLoading(false);
    }
  };

  const stopBriefing = async () => {
    if (chatRef.current) { await chatRef.current.stop(); chatRef.current = null; }
  };

  // Iter291 — Mic intelligent : 1 bouton, 2 états (idle 🎙️ / recording ➤)
  // Idle → click → enableUserMic + démarrage chrono → recording
  // Recording → click → commitAndSend + arrêt chrono → idle
  const toggleMic = () => {
    if (!chatRef.current) {
      toast.error('Session non active — relancez le briefing.');
      return;
    }
    if (micOpen) {
      // Recording → ENVOI immédiat (commit forcé du buffer + response.create)
      const ok = chatRef.current.commitAndSend();
      setMicOpen(false);
      setRecordingStart(null);
      setIncompleteReason(null);
      if (!ok) {
        toast.error('Impossible d\'envoyer le message vocal — relancez le briefing.');
      } else {
        toast.success('Question envoyée — l\'IA vous répond.');
      }
    } else {
      // Idle → ouverture mic
      chatRef.current.enableUserMic();
      setMicOpen(true);
      setRecordingStart(Date.now());
      setIncompleteReason(null);
      toast.message('🎙️ À vous — cliquez sur ➤ pour envoyer.');
    }
  };

  // Iter300 — Texte question : utilise le retour structuré {ok, reason} pour
  // afficher un toast adapté (réponse en cours / canal coupé / question vide).
  const submitTextQuestion = (text) => {
    if (!chatRef.current) {
      toast.error('Session non active — relancez le briefing.');
      return;
    }
    const t = String(text || '').trim();
    if (!t) return;
    const sectionLabel = SECTIONS_LABELS[focusedSection] || focusedSection;
    const r = chatRef.current.askQuestion(t, { section: sectionLabel });
    if (r && r.ok) {
      setTranscripts((prev) => [...prev, { role: 'user', text: t, streaming: false, ts: Date.now() }]);
      setAskOpen(false);
      setIncompleteReason(null);
    } else {
      const reason = r?.reason || 'unknown';
      const msg = reason === 'in_flight'
        ? 'Réponse en cours — patientez quelques secondes.'
        : reason === 'dc_closed'
          ? 'Connexion temps réel interrompue. Relancez le briefing.'
          : 'Impossible d\'envoyer la question.';
      toast.error(msg);
    }
  };

  // Iter289 — Bouton "Approfondir cette section" : question pré-formatée
  const deepenSection = () => {
    if (!chatRef.current) {
      toast.error('Session non active — relancez le briefing.');
      return;
    }
    const sectionLabel = SECTIONS_LABELS[focusedSection] || focusedSection;
    const q = `Approfondis la section actuelle : quel est le message clé, l'explication, l'implication business et l'action concrète à retenir ?`;
    const r = chatRef.current.askQuestion(q, { section: sectionLabel });
    if (r && r.ok) {
      setTranscripts((prev) => [...prev, { role: 'user', text: `🔍 Approfondir « ${sectionLabel} »`, streaming: false, ts: Date.now() }]);
      setIncompleteReason(null);
    } else {
      const reason = r?.reason || 'unknown';
      const msg = reason === 'in_flight'
        ? 'Réponse en cours — patientez quelques secondes.'
        : reason === 'dc_closed'
          ? 'Connexion temps réel interrompue. Relancez le briefing.'
          : 'Impossible d\'approfondir maintenant.';
      toast.error(msg);
    }
  };

  // Iter320 — UX simplifiée : aucun contrôle d'arrêt pendant la narration IA.
  // "Approfondir" devient simplement disabled pendant que l'IA parle, et se
  // réactive automatiquement à la fin (transition 'speaking' → 'ended_waiting_reset'
  // → 'idle' après 3s). Plus de Pause/Stop/Suspendre — UX honnête et fiable.

  // Iter314 — Annulation du mic en cours d'enregistrement (zéro envoi)
  const cancelMicRecording = () => {
    if (!chatRef.current) return;
    const ok = chatRef.current.cancelMicCapture();
    setMicOpen(false);
    setRecordingStart(null);
    setIncompleteReason(null);
    if (ok) toast.message('🛑 Enregistrement annulé.');
  };

  // Iter291 — Continuer la réponse précédente si elle a été tronquée
  const continueResponse = () => {
    if (!chatRef.current) return;
    const ok = chatRef.current.continueResponse();
    if (ok) {
      setIncompleteReason(null);
      toast.message('IA poursuit sa réponse…');
    } else {
      toast.error('Impossible de continuer maintenant.');
    }
  };

  // Iter289 — Navigation prev/next/direct
  const goToSection = (key) => {
    if (!key || !SECTIONS_LABELS[key]) return;
    setFocusedSection(key);
    setSectionsMenuOpen(false);
  };
  const goPrevSection = () => {
    const idx = SECTION_ORDER.indexOf(focusedSection);
    if (idx > 0) setFocusedSection(SECTION_ORDER[idx - 1]);
  };
  const goNextSection = () => {
    const idx = SECTION_ORDER.indexOf(focusedSection);
    if (idx >= 0 && idx < SECTION_ORDER.length - 1) setFocusedSection(SECTION_ORDER[idx + 1]);
  };

  useEffect(() => () => { chatRef.current?.stop?.(); }, []);

  /* ---------- Rendu ---------- */
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F1EA]">
        <Loader2 className="w-8 h-8 animate-spin text-[#92710A]" />
      </div>
    );
  }
  // Pas de return null silencieux — diagnostic explicite si le chargement a échoué
  if (loadError || !ctx || !batch || !report) {
    return (
      <div className="min-h-screen bg-[#F5F1EA] flex items-center justify-center p-8" data-testid="pulse-briefing-load-error">
        <div className="max-w-xl bg-white border border-[#E5DFD0] rounded-2xl p-8 text-[#1F1A12] shadow-sm">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#B94A48] font-semibold mb-2">
            Salle de restitution indisponible
          </div>
          <h2 className="font-serif text-2xl mb-3">Impossible d'ouvrir la salle.</h2>
          <p className="text-sm text-[#6B5E48] mb-4 leading-relaxed">
            {loadError || 'Données incomplètes (contexte, batch ou rapport manquant).'}
          </p>
          <button
            onClick={() => navigate(`/team-pulse/teams/${teamId}`)}
            className="inline-flex items-center gap-2 bg-[#1F1A12] text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-[#2A2319]"
            data-testid="pulse-briefing-load-error-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Retour au tableau de bord
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'preroom') {
    return (
      <PreSessionScreen
        ctx={ctx}
        onEnter={startBriefing}
        onBack={() => navigate(`/team-pulse/teams/${teamId}`)}
      />
    );
  }

  const stateInfo = STATE_LABELS[state] || STATE_LABELS.idle;
  const StateIcon = stateInfo.icon;
  const isLive = state !== 'idle';

  return (
    <div className="min-h-screen bg-[#F5F1EA] text-[#1F1A12]" data-testid="pulse-briefing-room">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-[#1F1A12]/95 text-white border-b border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 py-3 flex items-center gap-4">
          <button
            onClick={() => { stopBriefing(); navigate(`/team-pulse/teams/${teamId}`); }}
            className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-sm"
            data-testid="pulse-briefing-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
          <div className="h-5 w-px bg-white/15" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#F3D060]/80 font-semibold">
              COACHDIA · BRIEFING ROOM
            </div>
            <div className="font-serif text-lg leading-tight truncate">
              {ctx.team_name} <span className="text-white/50 text-sm">· Rapport v{ctx.wave_number}</span>
            </div>
          </div>
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold"
            style={{ background: `${stateInfo.color}20`, color: stateInfo.color, border: `1px solid ${stateInfo.color}55` }}
            data-testid="pulse-briefing-state"
            data-state={state}
          >
            <StateIcon className={`w-4 h-4 ${stateInfo.spin ? 'animate-spin' : ''}`} />
            {stateInfo.lbl}
          </div>
        </div>
      </header>

      {/* Main = Report View + IA panel */}
      <div className="max-w-[1600px] mx-auto lg:grid lg:grid-cols-[1fr_380px] lg:gap-6">

        {/* IA panel sticky */}
        <aside
          className="lg:order-2 lg:sticky lg:top-20 lg:self-start lg:max-h-[calc(100vh-5rem)] lg:overflow-y-auto p-4 lg:p-6 space-y-3 lg:space-y-4"
          data-testid="pulse-briefing-ai-panel"
        >
          <div className="bg-white rounded-2xl border border-[#E5DFD0] p-4 lg:p-5 shadow-[0_10px_30px_-20px_rgba(31,26,18,0.3)]">
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-all"
                style={{
                  background: isLive ? `${stateInfo.color}25` : COLORS.cream,
                  boxShadow: state === 'speaking' ? `0 0 0 6px ${stateInfo.color}25, 0 0 0 12px ${stateInfo.color}10` : 'none',
                }}
              >
                <StateIcon className={`w-5 h-5 ${stateInfo.spin ? 'animate-spin' : ''}`} style={{ color: stateInfo.color }} />
              </div>
              <div>
                <div className="text-[10px] tracking-wider uppercase text-[#92710A] font-semibold">Facilitateur</div>
                <div className="font-serif text-base leading-tight">{stateInfo.lbl}</div>
              </div>
            </div>

            {/* Iter290 — Boutons d'action déplacés exclusivement dans le CockpitBar (zone d'action unique premium). */}
          </div>

          {/* Transcription — dépliable mobile + desktop (Iter314 : default replié pour gagner de la place desktop) */}
          <div>
            <button
              type="button"
              onClick={() => setTranscriptOpen((o) => !o)}
              className="w-full flex items-center justify-between px-4 py-2.5 rounded-full bg-white border border-[#E5DFD0] hover:border-[#F3D060] transition text-[11px] uppercase tracking-wider text-[#92710A] font-semibold"
              data-testid="pulse-briefing-transcript-toggle"
              aria-expanded={transcriptOpen}
              aria-controls="pulse-briefing-transcript"
            >
              <span className="inline-flex items-center gap-2">
                <FileText className="w-3.5 h-3.5" />
                Transcription temps réel {transcripts.length > 0 ? `(${transcripts.length})` : ''}
              </span>
              <span className={`transition-transform inline-block ${transcriptOpen ? 'rotate-180' : ''}`}>⌄</span>
            </button>
          </div>
          <div
            id="pulse-briefing-transcript"
            className={`bg-white rounded-2xl border border-[#E5DFD0] p-4 max-h-[40vh] overflow-y-auto transition-all ${transcriptOpen ? 'block' : 'hidden'}`}
            data-testid="pulse-briefing-transcript"
          >
            {transcripts.length === 0 ? (
              <div className="text-xs text-[#8A7A5E] italic py-4 text-center">La conversation apparaîtra ici en direct.</div>
            ) : (
              <div className="space-y-3">
                {transcripts.map((t, i) => (
                  <div key={i} className={`text-xs leading-relaxed ${t.role === 'user' ? 'text-[#3B6B8F]' : 'text-[#1F1A12]'}`}>
                    <div className="text-[9px] uppercase tracking-wider text-[#92710A] font-semibold mb-0.5">
                      {t.role === 'user' ? 'Équipe' : 'IA'}
                    </div>
                    <div>
                      {t.text || '…'}
                      {t.streaming && <span className="inline-block w-1 h-3 bg-[#92710A]/70 ml-1 animate-pulse" />}
                    </div>
                  </div>
                ))}
                <div ref={transcriptEndRef} />
              </div>
            )}
          </div>
        </aside>

        {/* Report view — UI premium HTML, mode SINGLE (Iter289) */}
        <main className="lg:order-1 mt-4 lg:mt-0 lg:pb-32" data-testid="pulse-briefing-report-panel">
          <PulseReportView
            batch={batch}
            report={report}
            focusedSection={focusedSection}
            hideSidebarNav
            mode="single"
          />
        </main>
      </div>

      {/* Iter289 — COCKPIT BAR fixe en bas (premium navigation + actions) */}
      <CockpitBar
        focusedSection={focusedSection}
        sectionsMenuOpen={sectionsMenuOpen}
        onToggleMenu={() => setSectionsMenuOpen((v) => !v)}
        onCloseMenu={() => setSectionsMenuOpen(false)}
        onPrev={goPrevSection}
        onNext={goNextSection}
        onGoTo={goToSection}
        onAsk={() => setAskOpen(true)}
        onDeepen={deepenSection}
        onToggleMic={toggleMic}
        onEnd={endBriefing}
        onContinueResponse={continueResponse}
        onCancelMic={cancelMicRecording}
        playbackState={playbackState}
        micOpen={micOpen}
        recordingStart={recordingStart}
        incompleteReason={incompleteReason}
        state={state}
        connState={connState}
        onRetry={async () => {
          try { await chatRef.current?.stop?.(); } catch (err) { /* noop */ }
          setConnState('new');
          setPhase('preroom');
          setTranscripts([]);
        }}
      />

      {/* Iter289 — Dialog "Poser une question" (texte) */}
      {askOpen && (
        <AskQuestionDialog
          sectionLabel={SECTIONS_LABELS[focusedSection] || focusedSection}
          onClose={() => setAskOpen(false)}
          onSubmit={submitTextQuestion}
        />
      )}

      {synthesisOpen && (
        <SynthesisModal
          batchId={batchId}
          loading={synthLoading}
          synthesis={synthesis}
          onClose={() => setSynthesisOpen(false)}
          onDone={() => { setSynthesisOpen(false); navigate(`/team-pulse/teams/${teamId}`); }}
        />
      )}
    </div>
  );
}

/* ==================================================================
 * Synthesis Modal (Iter274 — préservé)
 * ================================================================== */
const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

function SynthesisModal({ batchId, loading, synthesis, onClose, onDone }) {
  const [recipients, setRecipients] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sentResult, setSentResult] = useState(null);

  const addEmails = (raw) => {
    if (!raw) return;
    const parts = String(raw).split(/[\s,;]+/).map((x) => x.trim().toLowerCase()).filter(Boolean);
    const next = [...recipients];
    let added = 0; let invalid = 0;
    for (const p of parts) {
      if (!EMAIL_RE.test(p)) { invalid += 1; continue; }
      if (next.includes(p)) continue;
      next.push(p); added += 1;
    }
    setRecipients(next);
    setInput('');
    if (added && invalid) toast.success(`${added} email(s) ajouté(s), ${invalid} invalide(s).`);
    else if (added) toast.success(`${added} email(s) ajouté(s).`);
    else if (invalid) toast.error(`${invalid} email(s) invalide(s).`);
  };

  const removeEmail = (e) => setRecipients((prev) => prev.filter((x) => x !== e));

  const onKeyDown = (ev) => {
    if (ev.key === 'Enter' || ev.key === ',' || ev.key === ';' || ev.key === 'Tab') {
      if (input.trim()) { ev.preventDefault(); addEmails(input); }
    }
  };

  const sendEmail = async () => {
    if (!synthesis || recipients.length === 0) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/pulse-briefing/send-synthesis/${batchId}`,
        { recipients, synthesis }, { headers: authHeaders() });
      setSentResult(r.data);
      if ((r.data?.count_sent || 0) > 0) toast.success(`Envoyé à ${r.data.count_sent} destinataire(s).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de l\'envoi.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#1F1A12]/80 backdrop-blur-sm p-4" data-testid="pulse-briefing-synthesis-modal">
      <div className="bg-[#F5F1EA] w-full max-w-3xl max-h-[90vh] rounded-3xl overflow-hidden shadow-2xl flex flex-col">
        <header className="bg-[#1F1A12] text-white px-7 py-5 flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#F3D060]/90 font-semibold">Synthèse de restitution</div>
            <h2 className="font-serif text-2xl leading-tight mt-1">
              {synthesis?.team_name || 'Votre équipe'}
              <span className="text-white/50 text-base"> · v{synthesis?.wave_number ?? '—'}</span>
            </h2>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="pulse-briefing-synthesis-close-btn">
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-16 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-[#92710A] mx-auto mb-4" />
              <p className="text-[#6B5E48] italic">Génération de la synthèse…</p>
            </div>
          ) : synthesis ? (
            <div className="p-7 space-y-6" data-testid="pulse-briefing-synthesis-body">
              <section>
                <h3 className="text-[10px] uppercase tracking-[0.3em] text-[#92710A] font-semibold mb-2">Résumé exécutif</h3>
                <p className="leading-relaxed text-[#1F1A12] bg-white border border-[#E5DFD0] rounded-2xl p-5">{synthesis.executive_summary}</p>
              </section>
              <SynthBlock title="Points clés" items={synthesis.key_points} icon={FileText} />
              {synthesis.questions_asked?.length > 0 && <SynthBlock title="Questions posées" items={synthesis.questions_asked} icon={AlertCircle} />}
              {synthesis.ai_clarifications?.length > 0 && <SynthBlock title="Clarifications" items={synthesis.ai_clarifications} icon={CheckCircle2} />}
              {synthesis.actions_proposed?.length > 0 && <SynthBlock title="Actions proposées" items={synthesis.actions_proposed} icon={Sparkles} />}

              <section className="bg-white border border-[#E5DFD0] rounded-2xl p-5">
                <h3 className="text-[10px] uppercase tracking-[0.3em] text-[#92710A] font-semibold mb-3 flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5" /> Envoyer la synthèse par email
                </h3>
                {recipients.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3" data-testid="pulse-briefing-recipient-chips">
                    {recipients.map((r) => (
                      <span key={r} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#F3D060]/20 border border-[#F3D060]/60 text-[#92710A] text-xs font-medium">
                        {r}
                        <button onClick={() => removeEmail(r)} className="hover:text-[#1F1A12]"><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <input type="text" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKeyDown}
                    placeholder="exemple@entreprise.com (Entrée, virgule)"
                    className="flex-1 px-4 py-2.5 rounded-full border border-[#E5DFD0] bg-[#FAF5EA] text-sm outline-none focus:border-[#92710A]"
                    data-testid="pulse-briefing-recipient-input"
                    disabled={sending}
                  />
                  <button onClick={() => addEmails(input)} disabled={!input.trim() || sending}
                    className="px-4 py-2.5 rounded-full bg-white border border-[#E5DFD0] text-[#1F1A12] text-sm font-medium hover:bg-[#FAF5EA] disabled:opacity-50"
                    data-testid="pulse-briefing-recipient-add-btn">Ajouter</button>
                </div>
                <div className="flex items-center justify-between mt-4">
                  <span className="text-xs text-[#6B5E48] italic">
                    {recipients.length === 0 ? 'Ajoutez au moins un destinataire.' : `${recipients.length} destinataire(s).`}
                  </span>
                  <button onClick={sendEmail} disabled={recipients.length === 0 || sending}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#F3D060] text-[#1F1A12] font-semibold hover:bg-[#E8C040] disabled:opacity-50"
                    data-testid="pulse-briefing-send-email-btn">
                    {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi…</> : <><Send className="w-4 h-4" /> Envoyer</>}
                  </button>
                </div>
                {sentResult && (
                  <div className="mt-4 p-3 rounded-xl bg-[#5B8A6B]/10 border border-[#5B8A6B]/30 text-[#2E5B3F] text-sm" data-testid="pulse-briefing-send-result">
                    <CheckCircle2 className="w-4 h-4 inline mr-1.5" />
                    Envoyé à {sentResult.count_sent} destinataire(s).
                  </div>
                )}
              </section>
            </div>
          ) : (
            <div className="p-10 text-center text-[#6B5E48] italic">Pas de synthèse disponible.</div>
          )}
        </div>

        <footer className="border-t border-[#E5DFD0] p-4 bg-white flex justify-between items-center">
          <button onClick={onClose} className="text-sm text-[#6B5E48] hover:text-[#1F1A12] px-3 py-2">Rester</button>
          <button onClick={onDone}
            className="bg-[#1F1A12] text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-[#2A2319] inline-flex items-center gap-2"
            data-testid="pulse-briefing-synthesis-done-btn">Retourner au dashboard</button>
        </footer>
      </div>
    </div>
  );
}

function SynthBlock({ title, items, icon: Icon }) {
  return (
    <section>
      <h3 className="text-[10px] uppercase tracking-[0.3em] text-[#92710A] font-semibold mb-2 flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" /> {title}
      </h3>
      <ul className="bg-white border border-[#E5DFD0] rounded-2xl p-5 space-y-2">
        {items.map((x, i) => (
          <li key={i} className="flex gap-2 text-[#1F1A12] leading-relaxed">
            <span className="text-[#F3D060] font-bold shrink-0">·</span><span>{x}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ==================================================================
 * Pre-session Screen
 * ================================================================== */
function PreSessionScreen({ ctx, onEnter, onBack }) {
  const [entering, setEntering] = useState(false);
  const handleEnter = async () => {
    setEntering(true);
    try { await onEnter(); } catch { setEntering(false); }
  };
  return (
    <div
      className="min-h-screen relative overflow-hidden"
      style={{ background: '#FAFAF8', color: '#1C1147' }}
      data-testid="pulse-briefing-preroom"
    >
      {/* Halos ambient or + violet (cohérent Iter304-313) */}
      <span
        aria-hidden
        className="absolute -top-32 -right-24 w-[520px] h-[520px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(212,175,55,0.18) 0%, rgba(212,175,55,0) 70%)',
          filter: 'blur(40px)',
        }}
      />
      <span
        aria-hidden
        className="absolute top-[40%] -left-40 w-[480px] h-[480px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(122,79,207,0.10) 0%, rgba(122,79,207,0) 70%)',
          filter: 'blur(50px)',
        }}
      />

      <header
        className="relative z-10 flex items-center justify-between px-6 sm:px-8 py-5"
        style={{ borderBottom: '1px solid #E8E4D9' }}
      >
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-[13px] font-medium transition-all"
          style={{
            background: '#FFFFFF',
            border: '1px solid #E8E4D9',
            color: '#1C1147',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7), 0 6px 16px -10px rgba(28,17,71,0.18)',
          }}
          data-testid="pulse-briefing-preroom-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>
        <div className="text-[10px] tracking-[0.30em] uppercase font-semibold" style={{ color: '#B7901F' }}>
          COACHDIA · Briefing Room
        </div>
      </header>

      <main className="relative z-10 flex flex-col items-center justify-center min-h-[calc(100vh-80px)] px-6 py-10 text-center">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-8 h-px" style={{ background: 'linear-gradient(90deg, transparent, #D4AF37)' }} />
          <span className="text-[11px] tracking-[0.22em] uppercase font-semibold" style={{ color: '#B7901F' }}>
            Salle de restitution
          </span>
          <span className="w-8 h-px" style={{ background: 'linear-gradient(90deg, #D4AF37, transparent)' }} />
        </div>
        <h1
          className="font-serif text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight mb-3 max-w-3xl"
          style={{
            background: 'linear-gradient(135deg, #1C1147 0%, #3A2E6E 60%, #B7901F 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 600,
            letterSpacing: '-0.02em',
          }}
        >
          {ctx.team_name}
          <span
            className="block text-2xl sm:text-3xl md:text-4xl mt-2"
            style={{
              background: 'linear-gradient(135deg, #B7901F 0%, #D4AF37 50%, #F3D060 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Rapport v{ctx.wave_number}
          </span>
        </h1>
        <p className="text-[15px] sm:text-base leading-relaxed max-w-xl mt-5 mb-8" style={{ color: '#5A5169' }}>
          Vous explorez votre rapport, section par section. Posez vos questions à l'oral ou par écrit — l'IA répond avec rigueur et profondeur, sans jamais vous interrompre.
        </p>

        <button
          onClick={handleEnter}
          disabled={entering}
          className="group relative inline-flex items-center gap-3 px-8 py-4 lg:px-10 lg:py-5 rounded-full font-semibold text-base lg:text-lg transition-all disabled:opacity-70 mb-10"
          style={{
            background: 'linear-gradient(135deg, #B7901F 0%, #D4AF37 50%, #F3D060 100%)',
            color: '#1C1147',
            border: '1px solid rgba(255,255,255,0.4)',
            boxShadow: '0 1px 0 rgba(255,255,255,0.4) inset, 0 18px 40px -12px rgba(212,175,55,0.6)',
          }}
          onMouseEnter={(e) => {
            if (entering) return;
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 1px 0 rgba(255,255,255,0.4) inset, 0 26px 56px -14px rgba(212,175,55,0.8)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 1px 0 rgba(255,255,255,0.4) inset, 0 18px 40px -12px rgba(212,175,55,0.6)';
          }}
          data-testid="pulse-briefing-enter-btn"
        >
          {entering ? <><Loader2 className="w-5 h-5 animate-spin" />Connexion en cours…</> : <><Sparkles className="w-5 h-5" />Entrer dans la salle</>}
        </button>

        <div className="grid md:grid-cols-3 gap-4 max-w-4xl w-full">
          <PreroomCard icon={Mic} title="Micro fermé par défaut" text="Vous décidez quand parler. Cliquez sur le toggle micro pour ouvrir/fermer à tout moment." />
          <PreroomCard icon={Volume2} title="Volume" text="Montez le son. Casque recommandé en réunion." />
          <PreroomCard icon={Radio} title="Sans interruption parasite" text="L'IA parle naturellement jusqu'au bout de son idée. Aucun arrêt automatique." />
        </div>
      </main>
    </div>
  );
}

function PreroomCard({ icon: Icon, title, text }) {
  return (
    <div
      className="rounded-2xl p-5 text-left transition-all"
      style={{
        background: '#FFFFFF',
        border: '1px solid #E8E4D9',
        boxShadow: '0 1px 0 rgba(28,17,71,0.04), 0 14px 30px -22px rgba(28,17,71,0.20)',
      }}
    >
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
        style={{
          background: 'linear-gradient(135deg, #FFFCEC 0%, #FFF4D2 100%)',
          border: '1px solid #E8E4D9',
        }}
      >
        <Icon className="w-5 h-5" style={{ color: '#B7901F' }} />
      </div>
      <div className="text-[10px] tracking-[0.22em] uppercase font-semibold" style={{ color: '#B7901F' }}>
        {title}
      </div>
      <div className="text-sm leading-relaxed mt-1" style={{ color: '#1C1147' }}>
        {text}
      </div>
    </div>
  );
}

/* ==================================================================
 * Iter289 — CockpitBar : barre fixe premium en bas
 * Navigation directe (menu sections + prev/next) + actions IA à la demande.
 * ================================================================== */
function CockpitBar({
  focusedSection, sectionsMenuOpen, onToggleMenu, onCloseMenu,
  onPrev, onNext, onGoTo, onAsk, onDeepen, onToggleMic, onEnd, onContinueResponse,
  onCancelMic,
  playbackState, micOpen, recordingStart, incompleteReason, state, connState, onRetry,
}) {
  const idx = SECTION_ORDER.indexOf(focusedSection);
  const total = SECTION_ORDER.length;
  const label = SECTIONS_LABELS[focusedSection] || focusedSection;
  // Iter320 — Source de vérité unique simplifiée :
  //   'idle'                → bouton Approfondir actif
  //   'speaking'            → IA parle, bouton Approfondir disabled (loader)
  //   'ended_waiting_reset' → IA terminée, grâce 3s avant idle (Approfondir encore disabled)
  // Aucun bouton Suspendre/Stop. Aucun timer fragile. Aucune logique parallèle.
  const isAiSpeaking =
    playbackState === 'speaking' ||
    playbackState === 'ended_waiting_reset';
  const disabledByState = state === 'connecting' || isAiSpeaking;

  // Iter291 — chrono live d'enregistrement (rafraîchit toutes les secondes)
  const [chronoTick, setChronoTick] = useState(0);
  useEffect(() => {
    if (!micOpen || !recordingStart) return undefined;
    const id = setInterval(() => setChronoTick((t) => t + 1), 500);
    return () => clearInterval(id);
  }, [micOpen, recordingStart]);
  const elapsedSec = (micOpen && recordingStart)
    ? Math.max(0, Math.floor((Date.now() - recordingStart) / 1000))
    : 0;
  const chronoLabel = `${String(Math.floor(elapsedSec / 60)).padStart(2, '0')}:${String(elapsedSec % 60).padStart(2, '0')}`;
  // Garde-fou : empêche un envoi avec moins d'1s d'audio
  const canSend = elapsedSec >= 1;
  // suppress no-unused-vars on chronoTick (used to force re-render)
  void chronoTick;
  return (
    <>
      {/* Menu sections (déroulant au-dessus du cockpit) */}
      {sectionsMenuOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={onCloseMenu}
            data-testid="pulse-briefing-sections-menu-backdrop"
          />
          <div
            className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 w-[min(92vw,540px)] bg-white rounded-3xl border border-[#E5DFD0] shadow-[0_30px_60px_-20px_rgba(31,26,18,0.4)] overflow-hidden"
            data-testid="pulse-briefing-sections-menu"
          >
            <div className="px-5 py-3 border-b border-[#E5DFD0] flex items-center justify-between bg-[#FBF8F1]">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#92710A] font-bold">
                Naviguer · 15 sections
              </div>
              <button onClick={onCloseMenu} className="text-[#8A7A5E] hover:text-[#1F1A12]">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto py-2">
              {SECTION_ORDER.map((k, i) => {
                const active = k === focusedSection;
                return (
                  <button
                    key={k}
                    onClick={() => onGoTo(k)}
                    className={`w-full flex items-center gap-3 px-5 py-2.5 text-left transition ${
                      active
                        ? 'bg-gradient-to-r from-[#F3D060]/30 to-transparent text-[#1F1A12] font-semibold'
                        : 'text-[#3D3525] hover:bg-[#FBF8F1]'
                    }`}
                    data-testid={`pulse-briefing-menu-item-${k}`}
                    data-active={active ? 'true' : 'false'}
                  >
                    <span className={`text-[10px] tabular-nums w-6 ${active ? 'text-[#92710A]' : 'text-[#8A7A5E]'}`}>
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="flex-1 text-sm">{SECTIONS_LABELS[k]}</span>
                    {active && <CircleDot className="w-3.5 h-3.5 text-[#92710A]" />}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}

      <div
        className="fixed bottom-0 left-0 right-0 z-30 backdrop-blur-md bg-white/95 border-t border-[#E5DFD0] shadow-[0_-10px_30px_-15px_rgba(31,26,18,0.2)]"
        data-testid="pulse-briefing-cockpit-bar"
        data-current-section={focusedSection}
      >
        {/* Iter318 — Cockpit responsive :
            • Mobile (<sm) : 2 rangées verticales stables
              - Row 1 : indicateur section + prev + next (compact)
              - Row 2 : Question + Approfondir/Pause/Stop + Mic + Continue + Health + End
            • Desktop (≥sm) : 1 rangée unique flex-nowrap
            Safe-area bottom préservée via padding-bottom env(safe-area-inset-bottom). */}
        <div
          className="max-w-[1600px] mx-auto px-3 sm:px-6 py-2.5 sm:py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 sm:flex-nowrap"
          style={{ paddingBottom: 'max(0.625rem, env(safe-area-inset-bottom))' }}
        >
          {/* ROW 1 (mobile) — Section nav | Desktop : segment de gauche */}
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 min-w-0">
            {/* Indicateur section + menu */}
            <button
              onClick={onToggleMenu}
              className="flex items-center gap-2 px-3 py-2 rounded-2xl bg-[#FBF8F1] border border-[#E5DFD0] hover:border-[#F3D060] hover:bg-[#F3D060]/10 transition group shrink min-w-0 flex-1 sm:flex-initial"
              data-testid="pulse-briefing-section-indicator"
              aria-expanded={sectionsMenuOpen}
            >
              <FileText className="w-4 h-4 text-[#92710A] shrink-0" />
              <span className="text-xs sm:text-sm font-semibold text-[#1F1A12] truncate flex-1 sm:flex-initial sm:max-w-[220px] text-left">
                {label}
              </span>
              <span className="text-[10px] tabular-nums text-[#8A7A5E] font-mono shrink-0">
                {idx + 1}/{total}
              </span>
            </button>

            {/* Prev / Next */}
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={onPrev}
                disabled={idx <= 0}
                className="w-9 h-9 rounded-full bg-white border border-[#E5DFD0] hover:bg-[#FBF8F1] hover:border-[#F3D060] flex items-center justify-center transition disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="pulse-briefing-prev-section-btn"
                aria-label="Section précédente"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <button
                onClick={onNext}
                disabled={idx >= total - 1}
                className="w-9 h-9 rounded-full bg-white border border-[#E5DFD0] hover:bg-[#FBF8F1] hover:border-[#F3D060] flex items-center justify-center transition disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="pulse-briefing-next-section-btn"
                aria-label="Section suivante"
              >
                <ArrowLeft className="w-4 h-4 rotate-180" />
              </button>
            </div>
          </div>

          {/* ROW 2 (mobile) — Actions IA | Desktop : segment de droite (ml-auto) */}
          <div
            className="flex items-center gap-2 sm:gap-2 flex-nowrap overflow-x-auto sm:overflow-visible scrollbar-none sm:ml-auto"
            data-testid="pulse-briefing-cockpit-actions"
          >
            {/* Actions IA à la demande */}
            <button
              onClick={onAsk}
              disabled={disabledByState || micOpen}
              className="px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-semibold border border-[#E5DFD0] bg-white text-[#1F1A12] hover:bg-[#FBF8F1] hover:border-[#F3D060] transition flex items-center gap-1.5 disabled:opacity-50 shrink-0"
              data-testid="pulse-briefing-cockpit-ask-btn"
            >
            <Send className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Poser une question</span>
            <span className="sm:hidden">Question</span>
          </button>
          {/* Iter320 — UX simplifiée : aucun bouton Suspendre/Stop pendant
              la narration IA. "Approfondir" reste affiché en permanence et
              passe en disabled pendant que l'IA parle (state 'speaking' ou
              'ended_waiting_reset'). À la fin réelle (3s post-fin),
              il redevient actif automatiquement. */}
          <button
            onClick={onDeepen}
            disabled={disabledByState || micOpen}
            className="px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-bold bg-gradient-to-r from-[#F3D060] via-[#E8C040] to-[#C8A64B] text-[#1F1A12] hover:from-[#F5D572] hover:to-[#D4B25A] transition flex items-center gap-1.5 shadow-[0_4px_14px_-4px_rgba(243,208,96,0.5)] disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
            data-testid="pulse-briefing-cockpit-deepen-btn"
            style={{ minWidth: '146px' }}
            aria-disabled={disabledByState || micOpen}
            aria-label={isAiSpeaking ? "L'IA termine son explication…" : 'Approfondir cette section'}
          >
            {isAiSpeaking ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span className="hidden sm:inline">Analyse en cours…</span>
                <span className="sm:hidden">…</span>
              </>
            ) : (
              <>
                <Sparkles className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Approfondir</span>
                <span className="sm:hidden">+ Approfondir</span>
              </>
            )}
          </button>
          {/* Iter291 — Bouton MIC INTELLIGENT (idle 🎙️ / recording ➤ Envoyer)
              Iter316 — shrink-0 sur tous les boutons pour layout stable */}
          {micOpen ? (
            <>
              <button
                onClick={onCancelMic}
                className="px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-semibold border border-[#B94A48]/40 bg-white text-[#B94A48] hover:bg-[#FDF2F2] hover:border-[#B94A48] transition flex items-center gap-1.5 shrink-0"
                data-testid="pulse-briefing-cockpit-cancel-mic-btn"
                aria-label="Annuler l'enregistrement vocal"
              >
                <X className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Annuler</span>
              </button>
              <button
                onClick={onToggleMic}
                disabled={!canSend}
                className="group relative px-4 sm:px-5 py-2.5 rounded-full bg-gradient-to-br from-[#B94A48] via-[#A93A38] to-[#8C2828] text-white shadow-[0_10px_30px_-8px_rgba(185,74,72,0.7)] flex items-center gap-2.5 border-2 border-[#B94A48] disabled:opacity-60 disabled:cursor-not-allowed transition-all overflow-hidden shrink-0"
                data-testid="pulse-briefing-cockpit-mic-btn"
                data-mic-state="recording"
                aria-label="Envoyer la question vocale"
              >
              <span className="absolute inset-0 bg-white/10 animate-pulse rounded-full" aria-hidden />
              {/* Mini waveform animée (3 barres) */}
              <span className="relative inline-flex items-end gap-0.5 h-4">
                <span className="w-0.5 bg-white rounded-full animate-[bounce_0.9s_ease-in-out_infinite]" style={{ height: '40%', animationDelay: '0ms' }} />
                <span className="w-0.5 bg-white rounded-full animate-[bounce_0.9s_ease-in-out_infinite]" style={{ height: '90%', animationDelay: '150ms' }} />
                <span className="w-0.5 bg-white rounded-full animate-[bounce_0.9s_ease-in-out_infinite]" style={{ height: '60%', animationDelay: '300ms' }} />
              </span>
              <span className="relative text-xs sm:text-sm font-bold tracking-wide">Envoyer</span>
              <span className="relative text-[10px] tabular-nums font-mono opacity-80" data-testid="pulse-briefing-recording-chrono">
                {chronoLabel}
              </span>
              <Send className="w-3.5 h-3.5 sm:w-4 sm:h-4 relative" />
            </button>
            </>
          ) : (
            <button
              onClick={onToggleMic}
              disabled={state === 'connecting'}
              className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-[#1F1A12] text-white hover:bg-[#2A2319] flex items-center justify-center border border-[#1F1A12] transition disabled:opacity-50 shrink-0"
              data-testid="pulse-briefing-cockpit-mic-btn"
              data-mic-state="idle"
              aria-label="Démarrer l'enregistrement vocal"
            >
              <Mic className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          )}

          {/* Iter391 — État discret "L'IA poursuit son analyse…" pour
              les auto-continue silencieux (incompleteReason préfixé "auto:").
              AUCUN bouton manuel exposé dans ce cas, juste un indicateur
              premium discret pour rassurer l'utilisateur. */}
          {incompleteReason && incompleteReason.startsWith('auto:') && !micOpen && (
            <div
              className="px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium border border-[#92710A]/30 bg-[#FBF4DC]/60 text-[#92710A] flex items-center gap-2 shrink-0"
              data-testid="pulse-briefing-autocontinue-indicator"
              aria-live="polite"
              role="status"
            >
              <span
                className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-pulse"
                aria-hidden
              />
              <span className="italic">L'IA poursuit son analyse…</span>
            </div>
          )}

          {/* Iter291 — Bouton "Continuer la réponse" (visible si tronquée
              ET pas en auto-continue) */}
          {incompleteReason && !incompleteReason.startsWith('auto:') && !micOpen && (
            <button
              onClick={onContinueResponse}
              disabled={disabledByState}
              className="px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-semibold border border-[#92710A]/50 bg-[#FBF4DC] text-[#1F1A12] hover:bg-[#F3D060]/30 hover:border-[#92710A] transition flex items-center gap-1.5 disabled:opacity-50 shrink-0"
              data-testid="pulse-briefing-cockpit-continue-btn"
              aria-label="Continuer la réponse précédente"
            >
              <Sparkles className="w-3.5 h-3.5" />
              <span>Continuer la réponse</span>
            </button>
          )}

          {/* Iter301 — Health dot + bouton Relancer si connexion KO */}
          <ConnectionHealthIndicator connState={connState} state={state} onRetry={onRetry} />

          {/* Iter290 — Bouton "Terminer la session" (déplacé depuis l'aside) */}
          <button
            onClick={onEnd}
            className="px-3 sm:px-4 py-2 rounded-full text-xs font-medium border border-[#E5DFD0] bg-white text-[#6B5E48] hover:bg-[#FBF8F1] hover:border-[#B94A48]/40 hover:text-[#B94A48] transition flex items-center gap-1.5 shrink-0"
            data-testid="pulse-briefing-cockpit-end-btn"
            aria-label="Terminer la session"
          >
            <Square className="w-3 h-3" />
            <span className="hidden sm:inline">Terminer</span>
          </button>
          </div>
        </div>
      </div>
    </>
  );
}

/* ==================================================================
 * Iter301 — Indicateur de santé connexion temps-réel
 * Vert  : connecté & idle/listening
 * Jaune : handshake / réponse en cours
 * Rouge : disconnected / failed → propose un Relancer
 * ================================================================== */
function ConnectionHealthIndicator({ connState, state, onRetry }) {
  const broken = connState === 'failed' || connState === 'disconnected' || connState === 'closed';
  const connecting = state === 'connecting' || connState === 'connecting' || connState === 'new';
  const color = broken ? '#B94A48' : connecting ? '#D4AF37' : '#48825A';
  const labelText = broken ? 'Connexion perdue' : connecting ? 'Connexion…' : 'En ligne';
  return (
    <div
      className="hidden sm:flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full bg-white/60 border border-[#E5DFD0]"
      data-testid="pulse-briefing-conn-health"
      data-conn-state={connState}
      title={`État WebRTC : ${connState}`}
    >
      <span
        className="w-2 h-2 rounded-full"
        style={{
          background: color,
          boxShadow: broken ? `0 0 8px ${color}AA` : 'none',
          animation: connecting ? 'pulse 1.4s ease-in-out infinite' : 'none',
        }}
      />
      <span className="text-[#6B5E48]">{labelText}</span>
      {broken && onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="ml-1 px-2 py-0.5 rounded-full text-[10px] bg-[#B94A48] text-white hover:bg-[#A53D3B] transition"
          data-testid="pulse-briefing-conn-retry"
        >
          Relancer
        </button>
      )}
    </div>
  );
}

/* ==================================================================
 * Iter289 — AskQuestionDialog : modale "Poser une question" (texte)
 * ================================================================== */
function AskQuestionDialog({ sectionLabel, onClose, onSubmit }) {
  const [text, setText] = useState('');
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  const submit = () => {
    const t = text.trim();
    if (t.length < 2) return;
    onSubmit(t);
  };
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={onClose}
      data-testid="pulse-briefing-ask-dialog"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:w-[min(92vw,560px)] bg-white rounded-t-3xl sm:rounded-3xl border border-[#E5DFD0] shadow-[0_30px_60px_-15px_rgba(31,26,18,0.5)] overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-[#E5DFD0] bg-[#FBF8F1] flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#92710A] font-bold">
              Poser une question · IA à la demande
            </div>
            <div className="font-serif text-base mt-0.5">Section : {sectionLabel}</div>
          </div>
          <button
            onClick={onClose}
            className="text-[#8A7A5E] hover:text-[#1F1A12]"
            data-testid="pulse-briefing-ask-dialog-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-3">
          <textarea
            ref={inputRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(); }}
            placeholder="Ex : « Quel est le vrai problème ici ? », « Quelle décision concrète prendre ? », « Qu'est-ce qui me menace le plus ? »"
            rows={4}
            className="w-full resize-none rounded-2xl border border-[#E5DFD0] bg-[#FBF8F1] px-4 py-3 text-[#1F1A12] placeholder:text-[#8A7A5E] focus:outline-none focus:ring-2 focus:ring-[#F3D060]"
            data-testid="pulse-briefing-ask-dialog-textarea"
          />
          <div className="flex items-center justify-between text-[10px] text-[#8A7A5E] font-mono">
            <span>⌘ + Entrée pour envoyer</span>
            <span>{text.length} car.</span>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#E5DFD0] bg-[#FBF8F1]/50 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-full text-sm font-medium text-[#8A7A5E] hover:text-[#1F1A12] hover:bg-[#FBF8F1] transition"
          >
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={text.trim().length < 2}
            className="px-5 py-2 rounded-full text-sm font-bold bg-[#1F1A12] text-white hover:bg-[#2A2319] disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center gap-1.5"
            data-testid="pulse-briefing-ask-dialog-submit"
          >
            <Send className="w-3.5 h-3.5" /> Envoyer
          </button>
        </div>
      </div>
    </div>
  );
}
